// src/hooks/useIncidents.js
// Primary hook for incident data. Components never touch the store or
// ingestion services directly.
//
// Provides:
//   incidents      — all active incidents, sorted by severity
//   active         — status is 'active' or 'mitigating'
//   critical       — severity >= 4
//   evacOrders     — incidents with mandatory/warning evacuationStatus
//   byType(type)   — filtered by incident type
//   loading        — initial load in progress
//   isStale        — data is older than TTL but displayed anyway (offline)
//   error          — last fetch error, classified ('offline'|'timeout'|msg)
//   refresh()      — force a fresh fetch bypassing cache
//   getIncident(id)— get one incident by id
//   getUpdates(id) — get the updates timeline for one incident

import { useState, useEffect, useCallback, useMemo } from 'react';
import { getDB } from '@services/storage/firebase';
import { collection, query, where, onSnapshot, orderBy, limit } from 'firebase/firestore';
import { useUserStore } from '@store/userStore';
import { useIncidentStore }                 from '@store/incidentStore';
import { fetchDOTDEvents, fetchUSGSExtended } from '@services/ingestion/dotd';
import { fetchNWSStateAlerts }               from '@services/ingestion/nws';
import {
  incidentFrom511,
  incidentFromGauge,
  incidentFromNWS,
}                                            from '@data/schemas/Incident';
import { APICache }                          from '@services/storage/idb';
import { REFRESH }                           from '@config';
import { LOW_WATER_CROSSINGS }               from '@data/static';

// Cache keys for IDB
const CACHE_KEY_511   = 'incidents-511la';
const CACHE_KEY_GAUGE = 'incidents-gauge';
const CACHE_KEY_NWS   = 'incidents-nws';

// ── Main hook ──────────────────────────────────────────────────────
export function useIncidents({ pollIntervalMs = REFRESH.traffic } = {}) {
  const zone  = useUserStore((s) => s.zone);
  const zones = useUserStore((s) => s.zones);
  // Declare store BEFORE useEffects that reference it
  const store = useIncidentStore();
  const [firestoreIncidents, setFirestoreIncidents] = useState([]);

  // Listen to Firestore road_incidents — subscribe once, no zone dep (limit=50)
  useEffect(() => {
    let unsub;
    (async () => {
      try {
        const db = await getDB();
        if (!db) return;
        const q = query(
          collection(db, 'road_incidents'),
          where('source', '==', 'dotd_511la'),
          limit(50)
        );
        unsub = onSnapshot(q, (snap) => {
          setFirestoreIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        });
      } catch (e) {
        console.warn('[useIncidents] Firestore listener failed:', e.message);
      }
    })();
    return () => unsub?.();
  }, []); // Empty deps — subscribe once only

  // Merge Firestore road_incidents into the store when they arrive
  // Use JSON key to prevent re-running on identical data (breaks render loop)
  const firestoreKey = firestoreIncidents.map(i => i.id).join(',');
  useEffect(() => {
    if (firestoreIncidents.length === 0) return;
    const normalized = firestoreIncidents.map(evt => ({
      incidentId:    evt.id,
      type:          (evt.EventType ?? evt.eventType ?? '').toLowerCase().includes('clos') ? 'road_closure' : 'other',
      status:        'active',
      source:        'dotd_511la',
      title:         evt.RoadwayName ?? evt.roadwayName ?? evt.road ?? 'Road Incident',
      description:   (evt.Description ?? evt.description ?? '').substring(0, 500),
      roadName:      evt.RoadwayName ?? evt.roadwayName ?? null,
      parish:        evt.County ?? evt.county ?? evt.Parish ?? null,
      geometry:      (evt.Latitude && evt.Longitude)
                       ? { type: 'point', lat: parseFloat(evt.Latitude), lon: parseFloat(evt.Longitude) }
                       : null,
      reportedAt:    evt.StartDate ?? evt.startDate ?? new Date().toISOString(),
      dotdIncidentId: String(evt.id ?? ''),
      lanesClosed:   evt.LanesAffected ?? evt.lanesAffected ?? null,
      ingestedAt:    evt.ingestedAt ?? null,
    }));
    store.mergeIncidents(normalized);
  }, [firestoreKey]); // Dep on stable ID key, not full object array
  const [loading,     setLoading]     = useState(false);


  const [error,       setError]       = useState(null);
  const [isStale,     setIsStale]     = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // ── Fetch from all three sources ───────────────────────────────
  const fetchAll = useCallback(async ({ forceNetwork = false } = {}) => {
    setError(null);

    // ── 1. IDB cache (immediate) ──────────────────────────────
    if (!forceNetwork) {
      const [c511, cGauge, cNws] = await Promise.all([
        APICache.get(CACHE_KEY_511),
        APICache.get(CACHE_KEY_GAUGE),
        APICache.get(CACHE_KEY_NWS),
      ]);

      const hasCached = c511?.data || cGauge?.data || cNws?.data;
      if (hasCached) {
        const cached = [
          ...(c511?.data    ?? []),
          ...(cGauge?.data  ?? []),
          ...(cNws?.data    ?? []),
        ];
        store.mergeIncidents(cached);
        setLastUpdated(Math.max(
          c511?.cachedAt ?? 0,
          cGauge?.cachedAt ?? 0,
          cNws?.cachedAt ?? 0,
        ));
        setLoading(false);
        const stale = (c511?.stale || cGauge?.stale || cNws?.stale) ?? false;
        setIsStale(stale);
        if (!stale) return;  // fresh — no network needed
      }
    }

    // ── 2. Network fetch (background if cache was present) ────
    store.setLoading(true);
    const [events511, gaugeData, nwsAlerts] = await Promise.allSettled([
      Promise.resolve([]),  // 511LA: server-side
      Promise.resolve([]),  // USGS: server-side
      Promise.resolve([]),  // NWS: handled by useAlerts
    ]);

    const incidents511 = events511.status === 'fulfilled'
      ? events511.value.map(incidentFrom511)
      : [];

    const incidentsGauge = gaugeData.status === 'fulfilled'
      ? LOW_WATER_CROSSINGS.flatMap((crossing) => {
          const gauge = gaugeData.value.find((g) => g.id === crossing.gaugeId);
          return gauge?.ft ? [incidentFromGauge(crossing, gauge.ft)] : [];
        })
      : [];

    const incidentsNWS = nwsAlerts.status === 'fulfilled'
      ? nwsAlerts.value
          .filter((a) => /flood|evacu/i.test(a.title ?? ''))
          .map(incidentFromNWS)
      : [];

    // Cache results
    await Promise.all([
      incidents511.length   ? APICache.set(CACHE_KEY_511,   incidents511,   REFRESH.traffic) : null,
      incidentsGauge.length ? APICache.set(CACHE_KEY_GAUGE, incidentsGauge, REFRESH.alerts)  : null,
      incidentsNWS.length   ? APICache.set(CACHE_KEY_NWS,   incidentsNWS,   REFRESH.alerts)  : null,
    ].filter(Boolean));

    const all = [...incidents511, ...incidentsGauge, ...incidentsNWS];
    store.mergeIncidents(all);
    store.setLoading(false);
    setLoading(false);
    setIsStale(false);
    setLastUpdated(Date.now());

    // Classify fetch error
    if (events511.status === 'rejected' && gaugeData.status === 'rejected') {
      setError(!navigator.onLine ? 'offline' : 'fetch_failed');
    }
  }, []); // Empty deps — store accessed at call time, not captured in closure

  // Mount + interval — with failure backoff to prevent proxy storm
  useEffect(() => {
    let failures = 0;
    let id;
    const safeFetch = async (opts) => {
      try {
        await fetchAll(opts);
        failures = 0;
      } catch {
        failures++;
      }
    };
    safeFetch({});
    // After 3 failures, back off to 30 min to stop hammering dead proxies
    id = setInterval(() => {
      if (failures >= 3) return;
      safeFetch({ forceNetwork: true });
    }, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetchAll, pollIntervalMs]);

  // Reconnect revalidation
  useEffect(() => {
    const h = () => fetchAll({ forceNetwork: true });
    window.addEventListener('online',            h);
    window.addEventListener('na-cache-updated',  h);
    return () => {
      window.removeEventListener('online',           h);
      window.removeEventListener('na-cache-updated', h);
    };
  }, [fetchAll]);

  // ── Derived sets ──────────────────────────────────────────────
  const sorted = useMemo(() =>
    [...(store.incidents ?? [])].sort((a, b) =>
      (b.severity ?? 1) - (a.severity ?? 1) ||
      (new Date(b.reportedAt ?? b.ingestedAt ?? 0).getTime() || 0) -
      (new Date(a.reportedAt ?? a.ingestedAt ?? 0).getTime() || 0)
    ),
    [store.incidents],
  );

  const active     = useMemo(() => sorted.filter((i) => ['active','mitigating'].includes(i.status)), [sorted]);
  const critical   = useMemo(() => sorted.filter((i) => i.severity >= 4), [sorted]);
  const evacOrders = useMemo(() => sorted.filter((i) => ['mandatory','warning'].includes(i.evacuationStatus) && i.status !== 'resolved'), [sorted]);

  return {
    incidents:    sorted,
    active,
    critical,
    evacOrders,
    loading,
    isStale,
    error,
    lastUpdated,
    byType:       (type)   => sorted.filter((i) => i.type === type),
    byParish:     (parish) => sorted.filter((i) => i.parish === parish || (i.affectedAreas ?? []).includes(parish)),
    getIncident:  (id)     => store.getById(id),
    getUpdates:   (id)     => store.getUpdates(id),
    refresh:      ()       => fetchAll({ forceNetwork: true }),
    // Store actions exposed for confirm/update writes (via incidentActions hook)
    _store:       store,
  };
}

// ── useIncident (single incident) ─────────────────────────────────
// For detail views — loads full updates timeline.
export function useIncident(incidentId) {
  const store   = useIncidentStore();
  const incident = store.getById(incidentId);
  const updates  = store.getUpdates(incidentId);
  const [loadingUpdates, setLoadingUpdates] = useState(false);

  useEffect(() => {
    if (!incidentId) return;
    // Load updates timeline from Firestore when detail view opens
    setLoadingUpdates(true);
    import('@services/storage/firebase').then(({ getDB }) => {
      const db = getDB();
      if (!db) { setLoadingUpdates(false); return; }
      import('firebase/firestore').then(({ collection, query, orderBy, limit, getDocs }) => {
        const q = query(
          collection(db, 'incidents', incidentId, 'updates'),
          orderBy('createdAt', 'desc'),
          limit(20),
        );
        getDocs(q).then((snap) => {
          const docs = snap.docs.map((d) => ({ ...d.data(), updateId: d.id }));
          store.setUpdates(incidentId, docs);
          setLoadingUpdates(false);
        }).catch(() => setLoadingUpdates(false));
      });
    });
  }, [incidentId, store]);

  return { incident, updates, loadingUpdates };
}

// ── useIncidentActions ─────────────────────────────────────────────
// Write operations — all optimistic, queued to sync engine.
export function useIncidentActions() {
  const store = useIncidentStore();

  const confirm = useCallback(async (incidentId, vote = 'confirm') => {
    // Optimistic
    store.confirmIncident(incidentId, vote);
    // Enqueue to sync engine
    const { enqueue } = await import('@services/sync/engine');
    await enqueue(vote === 'confirm' ? 'confirm_incident' : 'deny_incident', {
      incidentId,
      vote,
    }, { priority: 1, maxAttempts: 3 });
  }, [store]);

  const postUpdate = useCallback(async (incidentId, text, opts = {}) => {
    const { createIncidentUpdate } = await import('@data/schemas/Incident');
    const update = createIncidentUpdate(incidentId, {
      text,
      authorName:      opts.authorName ?? 'Anonymous',
      isOfficial:      opts.isOfficial ?? false,
      statusChange:    opts.statusChange    ?? null,
      evacuationChange:opts.evacuationChange ?? null,
      mediaUrls:       opts.mediaUrls ?? [],
    });
    // Optimistic
    store.addUpdate(incidentId, update);
    // Enqueue
    const { enqueue } = await import('@services/sync/engine');
    await enqueue('post_incident_update', { incidentId, update }, { priority: 2 });
    return update;
  }, [store]);

  const markResolved = useCallback(async (incidentId) => {
    store.updateStatus(incidentId, 'resolved');
    const { enqueue } = await import('@services/sync/engine');
    await enqueue('update_incident_status', {
      incidentId,
      status: 'resolved',
    }, { priority: 2 });
  }, [store]);

  return { confirm, postUpdate, markResolved };
}
